import type { SpaceKind } from '@core/categories'

/**
 * "Zaak" or "Huis", and what kind of cost it is.
 *
 * Two small facts that answer the question an administrator asks before any
 * other: is this mine or the restaurant's? Shown everywhere an item appears,
 * in the same shape each time, so the answer is recognised rather than read.
 *
 * The space is tinted and the category is not. They are not equally
 * important — mixing up a household and a business cost has consequences at
 * the accountant; a category being slightly wrong does not.
 */
export function SpaceTag({
  name,
  kind,
}: {
  name: string | null
  kind: SpaceKind | null
}) {
  if (!name) return null
  // tailwind.config.js already defines `household` and `restaurant` with the
  // note that the same colour means the same space everywhere. Reuse them
  // rather than inventing a second vocabulary for the same idea.
  const business = kind === 'business'
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${
        business ? 'bg-restaurant/10 text-restaurant' : 'bg-household/10 text-household'
      }`}
    >
      {name}
    </span>
  )
}

export function CategoryTag({ category }: { category: string | null }) {
  if (!category) return null
  return <span className="text-xs text-muted">{category}</span>
}

/** Both, with the separator only when both are actually there (rule 23). */
export function Tags({
  spaceName,
  spaceKind,
  category,
}: {
  spaceName: string | null
  spaceKind: SpaceKind | null
  category: string | null
}) {
  if (!spaceName && !category) return null
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <SpaceTag name={spaceName} kind={spaceKind} />
      {spaceName && category && <span className="text-xs text-muted">·</span>}
      <CategoryTag category={category} />
    </div>
  )
}
